'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductionStageForm, type ProductionStageFormRef } from '@/components/partials/production-stages'
import { ContentError, FormSkeleton } from '@/components/partials'
import { useProductionStage, useUpdateProductionStage } from '@/hooks'
import { type CreateProductionStageParams } from '@/types/production-stage'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productionStageFormRef = useRef<ProductionStageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: productionStage, isLoading, error } = useProductionStage(id)
  const { mutate: updateProductionStage, isPending } = useUpdateProductionStage()

  const handleSubmit = (data: CreateProductionStageParams) => {
    updateProductionStage(
      { id, params: data },
      {
        onSuccess: async updatedProductionStage => {
          setIsRedirecting(true)
          queryClient.setQueryData(['production-stage', id], updatedProductionStage)
          await queryClient.invalidateQueries({ queryKey: ['production-stages'] })
          toast.success(t('updated_successfully'))
          router.push(`/production-stages/${updatedProductionStage.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productionStageFormRef.current) {
        productionStageFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/production-stages/${id}`)
  }

  if (isLoading || !productionStage) {
    return <FormSkeleton fieldRows={1} showPictures showFiles />
  }

  if (error || !productionStage) {
    return <ContentError error={error} listUrl="/production-stages" />
  }

  return (
    <ProductionStageForm
      ref={productionStageFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={productionStage}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
